import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocalStorage } from './useLocalStorage';
import {
  SensitiveDataConfig,
  SensitiveFieldConfig,
  DEFAULT_SENSITIVE_DATA_CONFIG,
  PRESET_SENSITIVE_FIELDS,
} from '../types/sensitiveData';

const STORAGE_KEY = 'sensitive-data-config';

/**
 * Hook for managing sensitive data blurring
 */
export const useSensitiveDataBlur = () => {
  const [config, setConfig] = useLocalStorage<SensitiveDataConfig>(
    STORAGE_KEY,
    DEFAULT_SENSITIVE_DATA_CONFIG
  );
  const [isBlurActive, setIsBlurActive] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const revealTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  /**
   * Remove blur from all elements
   */
  const removeBlur = useCallback(() => {
    const blurredElements = document.querySelectorAll('[data-blurred="true"]');
    blurredElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.classList.remove('sensitive-data-blurred');
      htmlElement.style.filter = '';
      htmlElement.style.userSelect = '';
      htmlElement.removeAttribute('data-blurred');
    });
  }, []);

  /**
   * Apply blur to all configured sensitive fields
   */
  const applyBlur = useCallback(() => {
    if (!config.enabled || isRevealed) return;

    const enabledFields = config.fields.filter((field) => field.enabled);
    const blurIntensity = config.blurIntensity || 8;

    enabledFields.forEach((field) => {
      // Try selector first
      if (field.selector) {
        const elements = document.querySelectorAll(field.selector);
        elements.forEach((element) => {
          const htmlElement = element as HTMLElement;
          htmlElement.classList.add('sensitive-data-blurred');
          htmlElement.style.filter = `blur(${blurIntensity}px)`;
          htmlElement.style.userSelect = 'none';
          htmlElement.setAttribute('data-blurred', 'true');
        });
      }

      // Try data attribute
      if (field.dataAttribute) {
        const attrMatch = field.dataAttribute.match(
          /data-sensitive-field="([^"]+)"/
        );
        if (attrMatch) {
          const attrValue = attrMatch[1];
          const elements = document.querySelectorAll(
            `[data-sensitive-field="${attrValue}"]`
          );
          elements.forEach((element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.classList.add('sensitive-data-blurred');
            htmlElement.style.filter = `blur(${blurIntensity}px)`;
            htmlElement.style.userSelect = 'none';
            htmlElement.setAttribute('data-blurred', 'true');
          });
        }
      }
    });
  }, [config, isRevealed]);

  /**
   * Activate blurring (called when screenshot/bug report/AI assistant is triggered)
   */
  const activateBlur = useCallback(() => {
    if (!config.enabled) return;

    setIsBlurActive(true);
    setIsRevealed(false); // Reset reveal state when activating blur
    applyBlur();

    // Auto-deactivate after a short delay (in case screenshot capture is delayed)
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      // Keep blur active for at least 2 seconds to ensure screenshot captures it
      // The caller should call deactivateBlur when done
    }, 2000);
  }, [config.enabled, applyBlur]);

  /**
   * Deactivate blurring
   */
  const deactivateBlur = useCallback(() => {
    setIsBlurActive(false);
    setIsRevealed(false);
    removeBlur();
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
  }, [removeBlur]);

  /**
   * Hide revealed data and re-apply blur
   */
  const hideRevealedData = useCallback(() => {
    setIsRevealed(false);
    if (isBlurActive && config.enabled) {
      // Re-apply blur directly without checking isRevealed
      const enabledFields = config.fields.filter((field) => field.enabled);
      const blurIntensity = config.blurIntensity || 8;

      enabledFields.forEach((field) => {
        if (field.selector) {
          const elements = document.querySelectorAll(field.selector);
          elements.forEach((element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.classList.add('sensitive-data-blurred');
            htmlElement.style.filter = `blur(${blurIntensity}px)`;
            htmlElement.style.userSelect = 'none';
            htmlElement.setAttribute('data-blurred', 'true');
          });
        }

        if (field.dataAttribute) {
          const attrMatch = field.dataAttribute.match(
            /data-sensitive-field="([^"]+)"/
          );
          if (attrMatch) {
            const attrValue = attrMatch[1];
            const elements = document.querySelectorAll(
              `[data-sensitive-field="${attrValue}"]`
            );
            elements.forEach((element) => {
              const htmlElement = element as HTMLElement;
              htmlElement.classList.add('sensitive-data-blurred');
              htmlElement.style.filter = `blur(${blurIntensity}px)`;
              htmlElement.style.userSelect = 'none';
              htmlElement.setAttribute('data-blurred', 'true');
            });
          }
        }
      });
    }
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
  }, [isBlurActive, config]);

  /**
   * Reveal blurred data temporarily (requires re-authentication)
   */
  const revealBlurredData = useCallback(() => {
    if (!isBlurActive) return;

    setIsRevealed(true);
    removeBlur();

    // Auto-hide after 5 minutes for security
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
    }
    revealTimeoutRef.current = setTimeout(() => {
      hideRevealedData();
    }, 5 * 60 * 1000); // 5 minutes
  }, [isBlurActive, removeBlur, hideRevealedData]);

  /**
   * Update configuration
   */
  const updateConfig = useCallback(
    (newConfig: Partial<SensitiveDataConfig>) => {
      setConfig((prev) => ({ ...prev, ...newConfig }));
    },
    [setConfig]
  );

  /**
   * Update a specific field configuration
   */
  const updateField = useCallback(
    (fieldId: string, updates: Partial<SensitiveFieldConfig>) => {
      setConfig((prev) => ({
        ...prev,
        fields: prev.fields.map((field) =>
          field.id === fieldId ? { ...field, ...updates } : field
        ),
      }));
    },
    [setConfig]
  );

  /**
   * Reset to default configuration
   */
  const resetToDefault = useCallback(() => {
    setConfig(DEFAULT_SENSITIVE_DATA_CONFIG);
  }, [setConfig]);

  // Check for preview mode on mount and when config changes
  // This must be after applyBlur and removeBlur are defined
  // Preview mode is tab-specific (using sessionStorage and URL params)
  useEffect(() => {
    const checkPreviewMode = () => {
      // Check URL parameter first (for initial load)
      const urlParams = new URLSearchParams(window.location.search);
      const hasPreviewParam = urlParams.get('preview') === 'true';

      // Check sessionStorage (tab-specific storage)
      const previewMode = sessionStorage.getItem('sensitive-data-preview-mode');

      if (hasPreviewParam && !previewMode) {
        // Set sessionStorage if URL has preview param but storage doesn't
        sessionStorage.setItem('sensitive-data-preview-mode', 'true');
      }

      const isPreviewActive = previewMode === 'true' || hasPreviewParam;

      if (isPreviewActive && config.enabled) {
        // Auto-activate blur if preview mode is active
        setIsBlurActive(true);
        setIsRevealed(false);
        applyBlur();
      }
    };

    checkPreviewMode();

    // Also check periodically for same-tab navigation (URL might change)
    // Re-apply blur periodically to catch dynamically loaded content
    const interval = setInterval(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasPreviewParam = urlParams.get('preview') === 'true';
      const previewMode = sessionStorage.getItem('sensitive-data-preview-mode');
      const isPreviewActive = previewMode === 'true' || hasPreviewParam;

      if (isPreviewActive && config.enabled) {
        if (!isBlurActive) {
          checkPreviewMode();
        } else {
          // Re-apply blur to catch any new elements that were added
          applyBlur();
        }
      } else if (!isPreviewActive && isBlurActive) {
        setIsBlurActive(false);
        setIsRevealed(false);
        removeBlur();
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [
    config.enabled,
    config.fields,
    config.blurIntensity,
    isBlurActive,
    applyBlur,
    removeBlur,
  ]);

  // Watch for DOM changes and re-apply blur when new elements are added
  // This ensures blur is applied to dynamically loaded content
  useEffect(() => {
    if (!isBlurActive || isRevealed || !config.enabled) {
      // Clean up observer if blur is not active
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
      return;
    }

    // Create MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      // Debounce blur application to avoid excessive calls
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      blurTimeoutRef.current = setTimeout(() => {
        if (isBlurActive && !isRevealed && config.enabled) {
          applyBlur();
        }
      }, 100);
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    mutationObserverRef.current = observer;

    // Also apply blur immediately in case there are existing elements
    applyBlur();

    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, [
    isBlurActive,
    isRevealed,
    config.enabled,
    config.fields,
    config.blurIntensity,
    applyBlur,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      removeBlur();
    };
  }, [removeBlur]);

  return {
    config,
    isBlurActive,
    isRevealed,
    activateBlur,
    deactivateBlur,
    revealBlurredData,
    hideRevealedData,
    updateConfig,
    updateField,
    resetToDefault,
    applyBlur,
    removeBlur,
  };
};
